import { useTranslation } from 'react-i18next';
import { Flex, theme } from 'antd';
import { createPortal } from 'react-dom';
import { QuestionIcon } from '@phosphor-icons/react';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';
import {
  useOutOfDatePublicationVariant,
  type UseOutOfDatePublicationVariantArgs,
} from './useOutOfDatePublicationVariant';
import { useOutOfDatePublicationAction } from './useOutOfDatePublicationAction';
import { ACTION_KEYS, DISMISS_KEYS, HINT_KEYS } from './copyKeys';
import { IconButton } from '../IconButton';
import { radius } from '../../theme/radius';

export interface OutOfDatePublicationWidgetProps extends UseOutOfDatePublicationVariantArgs {
  /** Regenerates the media. */
  onRegenerate: () => Promise<void>;
  /** Updates the live publication. */
  onUpdate: () => Promise<void>;
  /**
   * Called when the user picks "Regenerate later" / "Update later" —
   * the caller is expected to persist this per-project (so the widget
   * stays dismissed across reloads) and render
   * `OutOfDatePublicationCompactButton` in its place. See MED-6969.
   */
  onDismiss: () => void;
  /** Translated hint for the "regenerate" variant; defaults to the video hint. */
  regenerateHint?: string;
}

/**
 * Floating call-to-action telling the user their edits have made the
 * project stale, with a single action to fix it. Derives which of the two
 * variants applies from the raw `isItemsOutOfDate`/`isPublicationOutOfDate`
 * flags itself (via `useOutOfDatePublicationVariant`) — callers just
 * forward their status, they don't map it to a variant or pick which
 * mutation to wire up.
 *
 * Renders centered near the bottom of the viewport (portalled to
 * `document.body`, so it floats above the page regardless of where it's
 * mounted) rather than as a toast — replaces the old
 * `OutOfDatePublicationToast`, which used to nag through a persistent,
 * non-dismissible-across-reloads toast notification.
 *
 * The caller must still gate rendering on `isItemsOutOfDate ||
 * isPublicationOutOfDate` and on its own persisted dismissal flag — this
 * component only decides *which* variant to show, not *whether* to mount.
 * Once dismissed, swap it for `OutOfDatePublicationCompactButton` instead
 * of unmounting to nothing, so the action stays reachable.
 *
 * @example
 * ```tsx
 * {(isItemsOutOfDate || isPublicationOutOfDate) &&
 *   (dismissed ? (
 *     <OutOfDatePublicationCompactButton
 *       isLoading={isLoading}
 *       isItemsOutOfDate={isItemsOutOfDate}
 *       isPublicationOutOfDate={isPublicationOutOfDate}
 *       onRegenerate={async () => {
 *         await regenerate();
 *         clearDismissal(projectId);
 *       }}
 *       onUpdate={async () => {
 *         await update();
 *         clearDismissal(projectId);
 *       }}
 *     />
 *   ) : (
 *     <OutOfDatePublicationWidget
 *       isLoading={isLoading}
 *       isItemsOutOfDate={isItemsOutOfDate}
 *       isPublicationOutOfDate={isPublicationOutOfDate}
 *       onRegenerate={regenerate}
 *       onUpdate={update}
 *       onDismiss={() => dismiss(projectId)}
 *     />
 *   ))}
 * ```
 */
export const OutOfDatePublicationWidget = ({
  isLoading,
  isItemsOutOfDate,
  isPublicationOutOfDate,
  onRegenerate,
  onUpdate,
  onDismiss,
  regenerateHint,
}: OutOfDatePublicationWidgetProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { token } = theme.useToken();
  const variant = useOutOfDatePublicationVariant({
    isLoading,
    isItemsOutOfDate,
    isPublicationOutOfDate,
  });
  const { isSubmitting, runAction } = useOutOfDatePublicationAction({
    variant,
    onRegenerate,
    onUpdate,
  });

  if (!variant) return null;

  const hint =
    variant === 'regenerate' && regenerateHint
      ? regenerateHint
      : t(HINT_KEYS[variant]);

  return createPortal(
    <StyledWidgetPosition role="status">
      {/* Painted first, so the pill below (a later sibling) naturally
        covers it — a child ::before would paint over the pill's own
        background instead, since positioned elements paint their own
        background before their negative-z-index children. */}
      <StyledGlow />
      <StyledPill $token={token}>
        <Flex align="center" gap={token.marginXS}>
          <Tooltip
            content={hint}
            placement="top"
            showDelay={300}
          >
            <IconButton
              variant="tertiary"
              size="s"
              icon={<QuestionIcon size={14} weight="bold" />}
              aria-label={hint}
              style={{
                border: `1px solid ${token.colorBorder}`,
                borderRadius: radius.circle,
              }}
            />
          </Tooltip>
          <Button
            variant="upgrade"
            size="l"
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={runAction}
          >
            {t(ACTION_KEYS[variant])}
          </Button>
          <Button
            variant="tertiary"
            size="m"
            onClick={onDismiss}
            disabled={isSubmitting}
          >
            {t(DISMISS_KEYS[variant])}
          </Button>
        </Flex>
      </StyledPill>
    </StyledWidgetPosition>,
    document.body,
  );
};

export default OutOfDatePublicationWidget;

// ─── Styled components ───

interface TokenProps {
  $token: GlobalToken;
}

// Positions the whole widget (glow + pill) as one unit — no background of
// its own, so it can't paint over the glow the way the pill's old
// ::before did.
const StyledWidgetPosition = styled.div`
  position: fixed;
  left: 50%;
  bottom: 32px;
  transform: translateX(-50%);
  z-index: 1000;
`;

// Wide ambient purple wash behind the pill, echoing the "upgrade" gradient
// it hosts. A radial gradient, not a box-shadow — a shadow only blurs a
// copy of the pill's own shape, it can't spread this far or fade this
// softly. Sized well past the pill and centered on it, absolutely
// positioned within StyledWidgetPosition so it doesn't affect flex layout.
const StyledGlow = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 900px;
  height: 560px;
  background: radial-gradient(
    closest-side,
    rgba(105, 70, 213, 0.55),
    rgba(105, 70, 213, 0.22) 45%,
    rgba(105, 70, 213, 0) 75%
  );
  pointer-events: none;
`;

const StyledPill = styled.div<TokenProps>`
  position: relative;
  padding: 12px 24px;
  background: ${({ $token }) => $token.colorBgContainer};
  border-radius: ${radius.pill}px;
  /* Small grounding shadow for the pill itself — the glow is the big wash
     behind it, not this. */
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.08);
`;
